import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { X, Upload, Paperclip, CheckCircle, Loader2, FileText, Download } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const DeepAnalysisModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    healthPercentage: 50,
    symptoms: '',
    medicalHistory: '',
    currentMedications: '',
    lifestyle: '',
    comments: ''
  });
  
  const [files, setFiles] = useState([]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({
          title: 'Ошибка',
          description: 'Необходима авторизация',
          variant: 'destructive'
        });
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('health_percentage', formData.healthPercentage);
      formDataToSend.append('symptoms', formData.symptoms);
      formDataToSend.append('medical_history', formData.medicalHistory);
      formDataToSend.append('current_medications', formData.currentMedications);
      formDataToSend.append('lifestyle', formData.lifestyle);
      formDataToSend.append('comments', formData.comments);
      
      files.forEach(file => {
        formDataToSend.append('files', file);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deep-analysis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Ошибка при создании анализа');
      }

      // Download PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AIMedGuru_Analysis_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Успешно!',
        description: 'Анализ готов и загружен'
      });

      // Reset form
      setFormData({
        healthPercentage: 50,
        symptoms: '',
        medicalHistory: '',
        currentMedications: '',
        lifestyle: '',
        comments: ''
      });
      setFiles([]);
      setStep(1);
      onClose();
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Ошибка',
        description: error.message || 'Не удалось создать анализ',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Шаг 1: Анкета здоровья</h3>
      </div>

      {/* Health Percentage Slider */}
      <div className="space-y-3">
        <label className="text-white/90 text-sm font-medium">
          Как бы вы оценили своё здоровье?
        </label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="0"
            max="100"
            value={formData.healthPercentage}
            onChange={(e) => setFormData({ ...formData, healthPercentage: parseInt(e.target.value) })}
            className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #ef4444 0%, #f59e0b ${formData.healthPercentage / 2}%, #10b981 100%)`
            }}
          />
          <div className="w-16 text-center">
            <span className="text-2xl font-bold text-white">{formData.healthPercentage}%</span>
          </div>
        </div>
        <div className="flex justify-between text-xs text-white/60">
          <span>Плохо</span>
          <span>Удовлетворительно</span>
          <span>Отлично</span>
        </div>
      </div>

      {/* Symptoms */}
      <div className="space-y-2">
        <label className="text-white/90 text-sm font-medium">Текущие симптомы и жалобы</label>
        <Textarea
          value={formData.symptoms}
          onChange={(e) => setFormData({ ...formData, symptoms: e.target.value })}
          placeholder="Опишите ваши симптомы, жалобы, что вас беспокоит..."
          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 rounded-xl min-h-[100px]"
        />
      </div>

      {/* Medical History */}
      <div className="space-y-2">
        <label className="text-white/90 text-sm font-medium">Медицинский анамнез</label>
        <Textarea
          value={formData.medicalHistory}
          onChange={(e) => setFormData({ ...formData, medicalHistory: e.target.value })}
          placeholder="Хронические заболевания, перенесенные операции, аллергии..."
          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 rounded-xl min-h-[80px]"
        />
      </div>

      {/* Current Medications */}
      <div className="space-y-2">
        <label className="text-white/90 text-sm font-medium">Текущие лекарства</label>
        <Textarea
          value={formData.currentMedications}
          onChange={(e) => setFormData({ ...formData, currentMedications: e.target.value })}
          placeholder="Какие лекарства принимаете, дозировки..."
          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 rounded-xl min-h-[60px]"
        />
      </div>

      {/* Lifestyle */}
      <div className="space-y-2">
        <label className="text-white/90 text-sm font-medium">Образ жизни</label>
        <Textarea
          value={formData.lifestyle}
          onChange={(e) => setFormData({ ...formData, lifestyle: e.target.value })}
          placeholder="Физическая активность, питание, сон, стресс, вредные привычки..."
          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 rounded-xl min-h-[60px]"
        />
      </div>

      <Button
        onClick={() => setStep(2)}
        className="w-full bg-gradient-to-r from-red-500/40 to-rose-500/40 hover:from-red-500/50 hover:to-rose-500/50 text-white border border-red-300/30 py-6 text-base rounded-full transition-all duration-300"
      >
        Далее: Загрузка документов
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Шаг 2: Загрузка документов</h3>
        <Button
          onClick={() => setStep(1)}
          variant="ghost"
          size="sm"
          className="text-white/60 hover:text-white hover:bg-white/10"
        >
          Назад
        </Button>
      </div>

      {/* Upload Area */}
      <div className="border-2 border-dashed border-white/30 rounded-2xl p-8 text-center hover:border-white/50 transition-all duration-300 cursor-pointer bg-white/5">
        <input
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
          id="file-upload"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="w-12 h-12 text-white/60 mx-auto mb-4" />
          <p className="text-white text-base mb-2">Перетащите файлы сюда</p>
          <p className="text-white/60 text-sm mb-4">или нажмите для выбора</p>
          <Button
            type="button"
            className="bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-full"
          >
            <Paperclip className="w-4 h-4 mr-2" />
            Выбрать файлы
          </Button>
        </label>
      </div>

      {/* Uploaded Files */}
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-white/80 text-sm font-medium">Загруженные файлы ({files.length}):</p>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-white/10 rounded-xl p-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-white/60 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{file.name}</p>
                    <p className="text-white/60 text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Supported Formats */}
      <div className="bg-white/5 border border-white/20 rounded-2xl p-4">
        <p className="text-white/80 text-sm font-medium mb-3">Поддерживаемые форматы:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {['PDF', 'JPG', 'PNG', 'DICOM', 'DOC', 'XLSX', 'HEIC', 'TIFF'].map((format) => (
            <div key={format} className="bg-white/10 rounded-lg px-3 py-2 text-center">
              <span className="text-white/80 text-xs">.{format.toLowerCase()}</span>
            </div>
          ))}
        </div>
      </div>

      <Button
        onClick={() => setStep(3)}
        className="w-full bg-gradient-to-r from-red-500/40 to-rose-500/40 hover:from-red-500/50 hover:to-rose-500/50 text-white border border-red-300/30 py-6 text-base rounded-full transition-all duration-300"
      >
        Далее: Комментарии
      </Button>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Шаг 3: Дополнительные комментарии</h3>
        <Button
          onClick={() => setStep(2)}
          variant="ghost"
          size="sm"
          className="text-white/60 hover:text-white hover:bg-white/10"
        >
          Назад
        </Button>
      </div>

      {/* Comments */}
      <div className="space-y-2">
        <label className="text-white/90 text-sm font-medium">
          Что еще важно знать AI для анализа?
        </label>
        <Textarea
          value={formData.comments}
          onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
          placeholder="Дополнительная информация, вопросы, специфические проблемы, на что обратить особое внимание..."
          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 rounded-xl min-h-[150px]"
        />
      </div>

      {/* Summary */}
      <div className="bg-white/5 border border-white/20 rounded-2xl p-4 space-y-3">
        <p className="text-white font-medium text-sm">Сводка:</p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-white/60">Оценка здоровья:</span>
            <span className="text-white">{formData.healthPercentage}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/60">Загружено файлов:</span>
            <span className="text-white">{files.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/60">Заполнено полей:</span>
            <span className="text-white">
              {[formData.symptoms, formData.medicalHistory, formData.currentMedications, formData.lifestyle, formData.comments]
                .filter(f => f.trim()).length} / 5
            </span>
          </div>
        </div>
      </div>

      {/* Price and Submit */}
      <div className="bg-gradient-to-r from-red-500/20 to-rose-500/20 border border-red-300/30 rounded-2xl p-6">
        <div className="flex flex-col items-center gap-4">
          <div className="text-center">
            <p className="text-white text-lg font-medium">Глубокий AI анализ</p>
            <div className="flex items-center justify-center gap-3 mt-2">
              <span className="text-xl line-through text-white/50">399 ₽</span>
              <span className="text-3xl font-bold text-red-400">299 ₽</span>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-red-500/40 to-rose-500/40 hover:from-red-500/50 hover:to-rose-500/50 text-white border border-red-300/30 px-8 py-6 text-base rounded-full transition-all duration-300 hover:scale-105"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Генерация анализа...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Получить PDF анализ
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="text-center">
        <p className="text-white/60 text-xs">
          🔒 Все данные защищены шифрованием • PDF будет загружен автоматически
        </p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-black/60 border border-white/30 rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/20">
          <div>
            <h2 className="text-xl md:text-2xl font-light text-white">Глубокий AI анализ здоровья</h2>
            <p className="text-white/60 text-sm mt-1">Шаг {step} из 3</p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-white/10">
          <div
            className="h-full bg-gradient-to-r from-red-500 to-rose-500 transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </div>
  );
};

export default DeepAnalysisModal;